import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { DatabaseService } from '../services/database.service';
import { LoggerService } from '../services/logger.service';
import { SyncInitService } from '../services/sync-init.service';

/**
 * Sync initialization guard
 *
 * Ensures sync service is initialized before accessing routes that need synced data.
 * This guard:
 * 1. Checks if user has an auth token
 * 2. If yes and sync not started, initializes sync once
 * 3. Allows navigation to proceed
 *
 * Applied to authenticated routes that need data (home, assets, team, etc.)
 * NOT applied to login/signup routes
 */
export const syncInitGuard: CanActivateFn = async (route, state) => {
  const guardStart = performance.now();
  const authService = inject(AuthService);
  const syncInitService = inject(SyncInitService);
  const databaseService = inject(DatabaseService);
  const router = inject(Router);
  const logger = inject(LoggerService).getLogger('sync-init-guard');

  logger.info('🚦 START - Route:', state.url);

  // Check if database is paused (non-leader tab) — allow navigation; overlay covers the UI
  if (databaseService.isPaused) {
    logger.info(
      '⏸ Database is paused (non-leader tab) - allowing navigation, overlay will cover UI',
    );
    return true;
  }

  // Check if database is in failed state - redirect to error page
  if (databaseService.isFailed) {
    logger.error('💀 Database is in FAILED state - redirecting to /error');
    await router.navigate(['/error']);
    return false;
  }

  // Check if user has a token
  const hasToken = !!authService.getToken();

  if (!hasToken) {
    logger.debug('No auth token - skipping sync initialization');
    return true; // Let authGuard handle redirect
  }

  // Start sync service (idempotent - handles multiple calls gracefully)
  // This will wait for schema to be applied before resolving
  logger.debug('🔄 Starting sync service...');
  try {
    const syncStart = performance.now();
    await syncInitService.start();
    const syncTime = (performance.now() - syncStart).toFixed(0);
    logger.debug(
      `✅ Sync service ready in ${syncTime}ms (schema applied, initial download complete)`,
    );

    // After start() resolves, the tab may be paused (non-leader) — check before
    // attempting the queryability check, which would throw on a paused DB.
    if (databaseService.isPaused) {
      logger.info('⏸ Database is paused after start() — non-leader tab, overlay will cover UI');
      return true;
    }

    // CRITICAL: Verify database is actually queryable before allowing navigation
    // The download_complete message means data was sent, but we need to ensure it's readable
    logger.debug('🔍 Verifying database is queryable...');
    try {
      // Try a simple query to verify database is ready
      logger.debug('🔍 Attempting: SELECT 1 as test');
      const testResult = await databaseService.exec('SELECT 1 as test', []);
      logger.debug('✅ Database is queryable, result:', testResult);
    } catch (dbError: any) {
      logger.error('❌ Database not queryable after sync:', dbError);
      logger.error('❌ Error details:', {
        message: dbError.message,
        name: dbError.name,
        stack: dbError.stack,
        cause: dbError.cause,
      });
      throw new Error(`Database not queryable after sync completion: ${dbError.message}`);
    }

    const guardTime = (performance.now() - guardStart).toFixed(0);
    logger.info(`✅ PASSED in ${guardTime}ms - allowing navigation to ${state.url}`);
    return true;
  } catch (error) {
    if (
      error instanceof Error &&
      error.message.includes('Sync service stopped - download promise cancelled')
    ) {
      logger.info(
        'Sync startup was intentionally cancelled during reconnect/reset; allowing navigation while restart proceeds',
      );
      return true;
    }

    logger.error('❌ Failed to initialize sync:', error);

    // Check if this caused database to enter failed state
    if (databaseService.isFailed) {
      logger.error('💀 Database FAILED - redirecting to /error');
      await router.navigate(['/error']);
      return false;
    }

    // Non-fatal sync error - allow navigation anyway (offline mode)
    logger.warn('⚠️ Sync error but database OK - allowing navigation (offline mode)');
    return true;
  }
};
